import React, { useEffect, useRef } from 'react';

interface WeatherCanvasProps {
  weatherType: 'leaves' | 'olive_leaves' | 'snow' | 'magic_orbs' | 'embers';
  expandToBottom?: boolean;
}

export const WeatherCanvas: React.FC<WeatherCanvasProps> = React.memo(({ weatherType, expandToBottom = false }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animationFrameId: number;
    let isDestroyed = false;

    let resizeTimer: number | null = null;
    const resize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (canvas && canvas.parentElement && !isDestroyed) {
          canvas.width = canvas.parentElement.clientWidth || window.innerWidth;
          const parentH = canvas.parentElement.clientHeight || (window.innerHeight * 0.55);
          canvas.height = expandToBottom ? Math.round(parentH * 1.85) : parentH;
        }
      }, 100);
    };
    // Initial direct size setup
    canvas.width = canvas.parentElement.clientWidth || window.innerWidth;
    const initialParentH = canvas.parentElement.clientHeight || (window.innerHeight * 0.55);
    canvas.height = expandToBottom ? Math.round(initialParentH * 1.85) : initialParentH;

    window.addEventListener('resize', resize, { passive: true });

    const baseCount = weatherType === 'snow' ? 32 : 20;
    const particleCount = expandToBottom ? Math.round(baseCount * 1.4) : baseCount;
    const particles = Array.from({ length: particleCount }).map(() => ({
      x: Math.random() * (canvas.width || 400),
      y: Math.random() * (canvas.height || 600),
      size: Math.random() * 2 + 1,
      speedX: (Math.random() - 0.5) * 1.4,
      speedY: Math.random() * 1.8 + 0.8,
      opacity: Math.random() * 0.4 + 0.5,
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.08
    }));

    // Pre-cache leaf sprite if weather is leaves
    let leafSprite: HTMLCanvasElement | null = null;
    if (weatherType === 'leaves' || weatherType === 'olive_leaves') {
      leafSprite = document.createElement('canvas');
      leafSprite.width = 32;
      leafSprite.height = 32;
      const lCtx = leafSprite.getContext('2d');
      if (lCtx) {
        lCtx.fillStyle = weatherType === 'olive_leaves' ? '#a3e635' : '#4ade80';
        lCtx.translate(16, 16);
        lCtx.beginPath();
        lCtx.ellipse(0, 0, 7, 3.5, 0, 0, Math.PI * 2);
        lCtx.fill();
      }
    }

    const render = () => {
      if (isDestroyed) return;

      if (document.hidden) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (weatherType === 'snow') {
        ctx.fillStyle = '#ffffff';
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          ctx.moveTo(p.x + p.size, p.y);
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

          p.y += p.speedY;
          p.x += Math.sin(p.y * 0.05) + p.speedX * 0.5;

          if (p.y > canvas.height + 15) p.y = -15;
          if (p.x > canvas.width + 15) p.x = -15;
          if (p.x < -15) p.x = canvas.width + 15;
        }
        ctx.fill();
      } else if (leafSprite && (weatherType === 'leaves' || weatherType === 'olive_leaves')) {
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          ctx.save();
          ctx.globalAlpha = p.opacity;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.drawImage(leafSprite, -16, -16);
          ctx.restore();

          p.rotation += p.rotSpeed;
          p.y += p.speedY * 0.8;
          p.x += Math.cos(p.y * 0.03) * 1.2 + p.speedX;

          if (p.y > canvas.height + 20) p.y = -20;
          if (p.x > canvas.width + 20) p.x = -20;
          if (p.x < -20) p.x = canvas.width + 20;
        }
      } else if (weatherType === 'magic_orbs') {
        ctx.fillStyle = '#e879f9';
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          ctx.moveTo(p.x + p.size + 1, p.y);
          ctx.arc(p.x, p.y, p.size + 1, 0, Math.PI * 2);

          p.y -= p.speedY * 0.5;
          p.x += Math.sin(p.y * 0.02) * 1.2;

          if (p.y < -20) p.y = canvas.height + 20;
          if (p.x > canvas.width + 20) p.x = -20;
          if (p.x < -20) p.x = canvas.width + 20;
        }
        ctx.fill();
      } else if (weatherType === 'embers') {
        ctx.fillStyle = '#fbbf24';
        ctx.globalAlpha = 0.75;
        ctx.beginPath();
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          ctx.moveTo(p.x + p.size, p.y);
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

          p.y -= p.speedY;
          p.x += (Math.random() - 0.5) * 1.5;

          if (p.y < -20) p.y = canvas.height + 20;
          if (p.x > canvas.width + 20) p.x = -20;
          if (p.x < -20) p.x = canvas.width + 20;
        }
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      isDestroyed = true;
      if (resizeTimer) clearTimeout(resizeTimer);
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
    };
  }, [weatherType, expandToBottom]);

  return (
    <canvas 
      ref={canvasRef} 
      className={`absolute inset-0 pointer-events-none z-30 ${expandToBottom ? 'h-[185%]' : 'h-full'}`} 
    />
  );
});
